import { IconButton } from "@sprout/icon_button";
import DropdownArrow from "@sprout/icons/dropdown_arrow";
import {
  type ClearIndicatorProps,
  components,
  type ControlProps,
  type GroupBase,
  type InputProps,
  type MultiValueRemoveProps,
} from "react-select";
import React, { type Ref } from "react";
import CloseIcon from "@sprout/icons/close";
import type { Grouped, Option } from "./types";

declare module "react-select/base" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,  @typescript-eslint/no-shadow
  export interface Props<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
    onKeyUp: React.KeyboardEventHandler;
    onMouseUp: React.MouseEventHandler;
    onTouchEnd: React.TouchEventHandler;
  }
}

export const ClearIndicator = ({
  clearValue,
  innerProps: { ref, ...restInnerProps },
}: ClearIndicatorProps<Option, boolean, Grouped<Option>>) => {
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      clearValue();
    }
  };

  // We want to wrap the onMouseDown handler so we can stop event propagation. If this event bubbles up,
  // it will cause the dropdown to open when clicking the clear indicator
  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!restInnerProps.onMouseDown) {
      return;
    }

    event.stopPropagation();

    restInnerProps.onMouseDown(event);
  };

  return (
    <div
      ref={ref as Ref<HTMLDivElement>}
      {...restInnerProps}
      onClick={onClick}
      onMouseUp={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onKeyDown={onKeyDown}
      aria-hidden={false}
      style={{ display: "flex", height: "fit-content" }}
    >
      <IconButton
        label="Clear Selections"
        icon={CloseIcon}
        onClick={(event) => event.preventDefault()}
      />
    </div>
  );
};

export const Control = (props: ControlProps<Option, boolean, Grouped<Option>>) => (
  <div
    onKeyUp={props.selectProps.onKeyUp}
    onMouseUp={props.selectProps.onMouseUp}
    onTouchEnd={props.selectProps.onTouchEnd}
  >
    <components.Control {...props} />
  </div>
);

export const DropdownIndicator = () => (
  <IconButton
    label="Toggle flyout"
    icon={DropdownArrow}
    onClick={(event) => event.preventDefault()}
  />
);

export const IndicatorSeparator = () => null;

export const Input = (props: InputProps<Option, boolean, Grouped<Option>>) => (
  <components.Input {...props} enterKeyHint="done" />
);

export const MultiValueRemove = (
  props: MultiValueRemoveProps<Option, boolean, Grouped<Option>>,
) => {
  return (
    <components.MultiValueRemove {...props}>
      <IconButton
        label="Remove Selection"
        icon={CloseIcon}
        size="sm"
        onClick={(event) => event.preventDefault()}
      />
    </components.MultiValueRemove>
  );
};
